<script lang="ts">
export interface ComboboxListOptionsProps<TOption, TValue> extends VirtualizerProps, ClassProps {
	options: ListOption<TOption, TValue>[];
	filter: string;
	customItem?: boolean;
}

export type ComboboxListOptionsEmit<TOption, TValue> = {
	select: [evt: { option: TOption; value: TValue }];
};
</script>

<script setup lang="ts" generic="TOption, TValue">
import { ComboboxViewport } from "reka-ui";
import type { ClassProps } from "../../lib/base-props";
import { useLocale } from "../../locale";
import { type ListOption, type OptionCustomItem, type OptionItem } from "../listbox/listbox";
import type { VirtualizerProps } from "../virtualizer/virtualizer";
import ComboboxCustomItem from "./ComboboxCustomItem.vue";
import ComboboxItem from "./ComboboxItem.vue";
import ComboboxVirtualizer from "./ComboboxVirtualizer.vue";

const { t } = useLocale();

const props = withDefaults(defineProps<ComboboxListOptionsProps<TOption, TValue>>(), {
	estimateHeight: 32,
	overscan: 5,
});
const emit = defineEmits<ComboboxListOptionsEmit<TOption, TValue>>();

defineSlots<{
	label?(slotProps: { label: string }): any;
	option?(slotProps: { option: TOption }): any;
	empty?(): any;
	customItem?(slotProps: { value: string }): any;
}>();

function handleSelect(option: OptionItem<TOption, TValue> | OptionCustomItem<TValue>) {
	if (option.type === "custom-item") {
		emit("select", { option: option.value as any, value: option.value });
	} else {
		emit("select", {
			option: option.option,
			value: option.value,
		});
	}
}
</script>

<template>
	<ComboboxViewport
		class="max-h-72 scroll-py-1 overflow-y-auto overflow-x-hidden p-1"
		:style="{
			minWidth: 'var(--reka-combobox-trigger-width)',
			maxWidth: 'var(--reka-combobox-trigger-width)',
		}"
	>
		<div v-if="options.length === 0 && !props.customItem" class="py-3 text-center text-sm">
			<slot name="empty">
				{{ t("table.no-result") }}
			</slot>
		</div>

		<ComboboxVirtualizer
			v-else
			:key="filter"
			v-slot="{ option }"
			:virtual="props.virtual"
			:overscan="props.overscan"
			:estimateHeight="props.estimateHeight"
			:options="options"
		>
			<ComboboxItem :option="option" @select="handleSelect as any">
				<template #label="slotProps">
					<slot name="label" v-bind="slotProps" />
				</template>

				<template #option="slotProps">
					<slot name="option" v-bind="slotProps" />
				</template>
			</ComboboxItem>
		</ComboboxVirtualizer>

		<ComboboxCustomItem
			v-if="props.customItem && filter"
			:value="filter"
			@select="(value) => handleSelect({ option: value, value } as any)"
		>
			<slot name="customItem" :value="filter">
				{{ filter }}
			</slot>
		</ComboboxCustomItem>
	</ComboboxViewport>
</template>
